import React, { useEffect } from 'react';
import { track } from '@amplitude/analytics-browser';
import {
  Box,
  Divider,
  Icon,
  Link,
  Text,
  Toggle,
  Tooltip,
} from '@nimbus-ds/components';
import { ExternalLinkIcon, InfoCircleIcon } from '@nimbus-ds/icons';
import { useStoreURL } from 'commons/hooks';
import { PaymentMethodsType } from 'commons/types';
import { useTranslationWithPrefix } from 'commons/utils';

interface CardContentProps {
  getIconForPaymentMethod: (method: PaymentMethodsType) => React.ReactNode;
  checkPaymentMethod: (
    enabled: boolean,
    paymentMethod: PaymentMethodsType,
  ) => void;
  isCardEnabled: boolean;
  isWalletEnabled: boolean;
}

function CardContent({
  getIconForPaymentMethod,
  checkPaymentMethod,
  isCardEnabled,
  isWalletEnabled,
}: CardContentProps): JSX.Element {
  const { t } = useTranslationWithPrefix('paymentMethods');
  const { buildAbsoluteStoreURL } = useStoreURL();

  useEffect(() => {
    // Wallet can only be enabled if card is enabled
    // so when card is disabled -> disable wallet
    if (!isCardEnabled && isWalletEnabled) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      checkPaymentMethod(false, PaymentMethodsType.Wallet);
    }
  }, [isCardEnabled, checkPaymentMethod, isWalletEnabled]);

  const onToggleWallet = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked;
    track('pagonube_config_mediosdepago_modo_click', {
      state_checkbox: checked ? 'enabled' : 'disabled',
    });
    checkPaymentMethod(checked, PaymentMethodsType.Wallet);
  };

  return (
    <Box display="flex" flexDirection="column" gap="2">
      <Text>{t('cardInstallmentsDescription')}</Text>
      <Link
        as="a"
        href={buildAbsoluteStoreURL('admin/nuvempago/#/installments')}
        target="_blank"
        appearance="primary"
      >
        {t('cardInstallmentsLink')}
        <ExternalLinkIcon size="small" />
      </Link>
      <Box marginTop="2" marginBottom="2">
        <Divider />
      </Box>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box display="flex" alignItems="center" flexDirection="row" gap="2">
          <Icon source={getIconForPaymentMethod(PaymentMethodsType.Wallet)} />
          <Text>{t('wallet')}</Text>
          {!isCardEnabled && (
            <Tooltip
              arrow
              position="bottom"
              content={t('walletTooltip')}
              maxWidth="160px"
            >
              <Icon source={<InfoCircleIcon />} data-testid="info-icon" />
            </Tooltip>
          )}
        </Box>
        <Toggle
          checked={isWalletEnabled && isCardEnabled}
          disabled={!isCardEnabled}
          onChange={onToggleWallet}
          name={PaymentMethodsType.Wallet}
          aria-label={t('wallet')}
        />
      </Box>
      {isCardEnabled && <Text>{t('walletDescription')}</Text>}
      {!isCardEnabled && <Text>{t('walletTooltip')}</Text>}
    </Box>
  );
}

export default CardContent;
